import { fixedUi } from '@/i18n/fixedUi';
import { useCallback, useState } from 'react';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import {
  buildMaterialTemplateExportBundle,
  downloadCsvFromObjects,
  downloadJson,
  fetchTemplatesForExport,
  importMaterialTemplates,
  materialTemplateTypeLabel,
  parseMaterialTemplateImportBundle,
  pickJsonFile,
} from '@/shein-platform/utils/sheinMaterialTemplatePortable';
import { portableItemsToCreateBodies } from '@/shein-platform/utils/sheinOfficialTemplateBatchImport';
import { showToast } from '@/utils/toast';

/**
 * 素材模板列表页共用：CSV / JSON 导出 + JSON 导入。
 * @param {() => Promise<string|null>} [resolveShopId] 全部视图下导入前解析目标店
 * @param {(body: object, ctx: { shopId: string }) => Promise<any>} createOne
 * @param {(payload: object, ctx: { shopId: string }) => Promise<any>} [batchCreate]
 */
export default function useMaterialTemplatePortableActions({
  type,
  shopId,
  resolveShopId,
  filePrefix,
  resolveExportRows,
  resolvePageRows,
  resolveSelectedRows,
  fetchFilteredRows,
  buildCsvRows,
  getOne,
  listByProductType,
  createOne,
  batchCreate,
  importTargetLabel = '当前店铺',
  onImported,
}) {
  const [porting, setPorting] = useState(false);
  const typeLabel = materialTemplateTypeLabel(type);

  const resolveImportShopId = useCallback(async () => {
    if (typeof resolveShopId === 'function') {
      return resolveShopId();
    }
    return shopId || null;
  }, [resolveShopId, shopId]);

  const writeCsv = useCallback((rows) => {
    const exportRows = typeof buildCsvRows === 'function' ? buildCsvRows(rows) : [];
    if (!exportRows.length) {
      showToast(fixedUi("暂无数据可导出"), 'warning');
      return;
    }
    downloadCsvFromObjects(exportRows, `${filePrefix || typeLabel}_${Date.now()}.csv`);
    showToast(`已导出 ${exportRows.length} 条`, 'success');
  }, [buildCsvRows, filePrefix, typeLabel]);

  const onExportCsv = useCallback(() => {
    const rows = typeof resolveExportRows === 'function' ? resolveExportRows() : [];
    writeCsv(rows);
  }, [resolveExportRows, writeCsv]);

  const onExportPageCsv = useCallback(() => {
    const rows = typeof resolvePageRows === 'function'
      ? resolvePageRows()
      : (typeof resolveExportRows === 'function' ? resolveExportRows() : []);
    writeCsv(rows);
  }, [resolvePageRows, resolveExportRows, writeCsv]);

  const onExportSelectedCsv = useCallback(() => {
    const rows = typeof resolveSelectedRows === 'function' ? resolveSelectedRows() : [];
    if (!rows.length) {
      showToast(fixedUi("请先勾选要导出的数据"), 'warning');
      return;
    }
    writeCsv(rows);
  }, [resolveSelectedRows, writeCsv]);

  const onExportFilteredCsv = useCallback(async () => {
    if (typeof fetchFilteredRows !== 'function') {
      onExportPageCsv();
      return;
    }
    setPorting(true);
    try {
      const rows = await fetchFilteredRows();
      writeCsv(Array.isArray(rows) ? rows : []);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setPorting(false);
    }
  }, [fetchFilteredRows, onExportPageCsv, writeCsv]);

  const onExportJson = useCallback(async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    const selected = typeof resolveSelectedRows === 'function' ? resolveSelectedRows() : [];
    const rows = selected.length
      ? selected
      : (typeof resolveExportRows === 'function' ? resolveExportRows() : []);
    if (!rows.length) {
      showToast(fixedUi("暂无数据可导出"), 'warning');
      return;
    }
    setPorting(true);
    try {
      const details = await fetchTemplatesForExport(rows, getOne);
      const bundle = buildMaterialTemplateExportBundle({
        type,
        shopId,
        items: details,
      });
      downloadJson(bundle, `${filePrefix || typeLabel}_${Date.now()}.json`);
      showToast(`已导出完整 JSON ${details.length} 条`, 'success');
    } catch (e) {
      showToast(e?.message || '导出失败', 'error');
    } finally {
      setPorting(false);
    }
  }, [shopId, resolveSelectedRows, resolveExportRows, getOne, type, filePrefix, typeLabel]);

  const onImportJson = useCallback(async () => {
    const sid = await resolveImportShopId();
    if (!sid) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    let picked;
    try {
      picked = await pickJsonFile();
    } catch (e) {
      // 用户取消选择时不提示
      if (e?.message && e.message !== '未选择文件') {
        showToast(e.message, 'error');
      }
      return;
    }
    let bundle;
    try {
      bundle = parseMaterialTemplateImportBundle(picked.data, type);
    } catch (e) {
      showToast(e?.message || '文件校验失败', 'error');
      return;
    }
    const confirmed = await showSheinConfirm({
      title: `导入${typeLabel}`,
      message: `将导入 ${bundle.items.length} 条到${importTargetLabel}。优先级会自动重算以避免冲突，模板名称保持文件中的原名。是否继续？`,
      confirmText: '开始导入',
      cancelText: '取消',
      tone: 'warning',
    });
    if (!confirmed) return;

    const ctx = { shopId: String(sid) };
    setPorting(true);
    try {
      let result;
      if (typeof batchCreate === 'function') {
        const res = await batchCreate({
          items: portableItemsToCreateBodies(type, bundle.items),
        }, ctx);
        const failed = Array.isArray(res?.failed) ? res.failed : [];
        result = {
          ok: Number(res?.created) || 0,
          failed: failed.length,
          errors: failed.map((row) => ({
            index: row.index,
            name: row.name,
            message: row.message,
          })),
        };
      } else {
        result = await importMaterialTemplates({
          type,
          items: bundle.items,
          listByProductType: listByProductType
            ? (productTypeId) => listByProductType(productTypeId, ctx)
            : undefined,
          createOne: (body) => createOne(body, ctx),
        });
      }
      if (result.ok > 0) {
        await onImported?.();
      }
      if (result.failed === 0) {
        showToast(`成功导入 ${result.ok} 条`, 'success');
      } else if (result.ok === 0) {
        showToast(result.errors[0]?.message || '导入失败', 'error');
      } else {
        showToast(`导入完成：成功 ${result.ok}，失败 ${result.failed}`, 'warning');
      }
    } catch (e) {
      showToast(e?.message || '导入失败', 'error');
    } finally {
      setPorting(false);
    }
  }, [
    resolveImportShopId,
    type,
    typeLabel,
    listByProductType,
    createOne,
    batchCreate,
    importTargetLabel,
    onImported,
  ]);

  return {
    porting,
    onExportCsv,
    onExportFilteredCsv,
    onExportPageCsv,
    onExportSelectedCsv,
    onExportJson,
    onImportJson,
  };
}
